// SUPPORT-001 / SYSTEM-001 console batch 1 (owner, 2026-10-25; D-067): the support desk (shops' Help
// centre tickets answered from the console), shops' ideas with votes, What's new, the setup steps a new
// shop goes through and the shop health score. The rules are pure functions here, so the server and the
// dev mocks score shops the same way.
import type { OrgLifecycleState, PlanIcon } from "./plan-features";

const DAY = 86_400_000;

// ---------- support desk ----------

export const DESK_STATES = ["open", "answered", "closed"] as const;
export type DeskState = (typeof DESK_STATES)[number];
export const DESK_PRIORITIES = ["low", "normal", "high"] as const;
export type DeskPriority = (typeof DESK_PRIORITIES)[number];
export const DESK_CATEGORIES = ["question", "problem", "billing", "feature"] as const;
export type DeskCategory = (typeof DESK_CATEGORIES)[number];

export interface DeskTicketRow {
  id: string;
  org_id: string;
  org_name: string;
  plan_name: string;
  plan_icon: PlanIcon;
  category: DeskCategory;
  subject: string;
  state: DeskState;
  priority: DeskPriority;
  assignee_id: string | null;
  assignee_name: string | null;
  sla_due_at: string | null;
  /** Open, never answered by staff and past the first-response target. */
  overdue: boolean;
  /** Who wrote the last message the shop can see. */
  last_from: "vendor" | "staff";
  snippet: string;
  messages: number;
  created_at: string | null;
  updated_at: string | null;
}

export interface DeskStaff {
  id: string;
  name: string;
  role: string;
}

export interface DeskBoard {
  rows: DeskTicketRow[];
  counts: {
    open: number;
    answered: number;
    closed: number;
    overdue: number;
    unassigned: number;
    mine: number;
  };
  staff: DeskStaff[];
  me: string;
}

export interface DeskMessage {
  id: string;
  author_type: "vendor" | "staff";
  author_name: string | null;
  body: string;
  /** A staff note the shop never sees. */
  internal: boolean;
  created_at: string | null;
}

export interface DeskTicket {
  ticket: DeskTicketRow & {
    context: Record<string, string> | null;
    store_name: string | null;
    first_response_at: string | null;
  };
  messages: DeskMessage[];
  shop: {
    id: string;
    name: string;
    slug: string;
    lifecycle: OrgLifecycleState;
    plan_name: string;
    plan_icon: PlanIcon;
    owner_email: string | null;
    last_active_at: string | null;
    health: ShopHealth;
  };
  /** The shop's other tickets, newest first. */
  others: { id: string; subject: string; state: DeskState; updated_at: string | null }[];
  staff: DeskStaff[];
}

/** Help guide topics (the vendor Help centre's topic cards). */
export const HELP_MODULES = [
  "start",
  "products",
  "orders",
  "delivery",
  "payments",
  "promotions",
  "store",
  "customers",
  "settings",
] as const;
export type HelpModule = (typeof HELP_MODULES)[number];

// ---------- ideas (feature requests) ----------

export const IDEA_STATUSES = [
  "new",
  "under_review",
  "planned",
  "in_progress",
  "shipped",
  "declined",
] as const;
export type IdeaStatus = (typeof IDEA_STATUSES)[number];

/** Console view: who sent it and who voted (organisation names only). */
export interface IdeaView {
  id: string;
  title: string;
  body: string;
  status: IdeaStatus;
  /** Other shops can see and vote on it (never with the sender's name). */
  public: boolean;
  votes: number;
  paying_votes: number;
  org_id: string | null;
  org_name: string | null;
  response: string | null;
  registry_id: string | null;
  merged_into: string | null;
  release_id: string | null;
  release_title: string | null;
  voters: { org_id: string; name: string }[];
  created_at: string | null;
  updated_at: string | null;
}

export interface IdeaBoard {
  rows: IdeaView[];
  counts: Record<IdeaStatus, number>;
}

/** Vendor view: public ideas plus the shop's own. */
export interface ShopIdea {
  id: string;
  title: string;
  body: string;
  status: IdeaStatus;
  votes: number;
  voted: boolean;
  mine: boolean;
  response: string | null;
  release_title: string | null;
  created_at: string | null;
}

// ---------- What's new (release notes) ----------

export const RELEASE_TAGS = ["new", "improved", "fixed"] as const;
export type ReleaseTag = (typeof RELEASE_TAGS)[number];

export interface ReleaseNoteView {
  id: string;
  title: string;
  body: string;
  tag: ReleaseTag;
  /** An admin path ("/products") or an https link. */
  link: string | null;
  /** The plan feature it belongs to: shops without it see the lock and the plan that has it. */
  feature_key: string | null;
  published: boolean;
  published_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  ideas: { id: string; title: string; votes: number }[];
  /** People who opened What's new after it was published. */
  seen: number;
}

export interface ShopRelease {
  id: string;
  title: string;
  body: string;
  tag: ReleaseTag;
  link: string | null;
  feature_key: string | null;
  published_at: string | null;
  new: boolean;
}

export interface ShopWhatsNew {
  data: ShopRelease[];
  unseen: number;
}

// ---------- setup steps (onboarding tracker) ----------

export const SETUP_STEPS = [
  "store_live",
  "products",
  "verified",
  "domain",
  "channels",
  "first_order",
  "wallet",
] as const;
export type SetupStep = (typeof SETUP_STEPS)[number];

/** Where a nudge sends the shop (an admin path under /{org}/{store}). */
export const SETUP_PATHS: Record<SetupStep, string> = {
  store_live: "/store",
  products: "/products",
  verified: "/settings",
  domain: "/store/domains",
  channels: "/conversations",
  first_order: "/marketing/links",
  wallet: "/wallet",
};

/** What the platform database knows about a shop's setup (no shard reads). */
export interface SetupFacts {
  store_live: boolean;
  products: number;
  kyc_state: string | null;
  custom_domain: boolean;
  /** Sampark channels and marketplaces connected. */
  channels: number;
  /** Orders ever (any month). */
  orders: number;
  /** Confirmed wallet top-ups. */
  wallet_topups: number;
}

export function setupDone(f: SetupFacts): SetupStep[] {
  const done: Record<SetupStep, boolean> = {
    store_live: f.store_live,
    products: f.products > 0,
    verified: f.kyc_state === "approved",
    domain: f.custom_domain,
    channels: f.channels > 0,
    first_order: f.orders > 0,
    wallet: f.wallet_topups > 0,
  };
  return SETUP_STEPS.filter((s) => done[s]);
}

export const setupPercent = (done: readonly SetupStep[]) =>
  Math.round((done.length / SETUP_STEPS.length) * 100);

export interface OnboardingRow {
  org_id: string;
  name: string;
  slug: string;
  lifecycle: OrgLifecycleState;
  plan_name: string;
  plan_icon: PlanIcon;
  created_at: string | null;
  last_active_at: string | null;
  done: SetupStep[];
  /** The first step not done yet. */
  next: SetupStep | null;
  percent: number;
  nudged_at: string | null;
  nudged_step: SetupStep | null;
}

export interface OnboardingBoard {
  rows: OnboardingRow[];
  /** Shops that finished each step. */
  funnel: { step: SetupStep; shops: number }[];
  total: number;
  complete: number;
}

/** Daily order counts from month-to-date snapshots (org_usage_daily.orders, YYYY-MM-DD dates). */
export function dailyOrders(
  rows: readonly { date: string; orders: number }[],
): Map<string, number> {
  const sorted = [...rows].sort((a, b) => a.date.localeCompare(b.date));
  const out = new Map<string, number>();
  let prev: { date: string; orders: number } | null = null;
  for (const r of sorted) {
    const sameMonth = prev !== null && prev.date.slice(0, 7) === r.date.slice(0, 7);
    out.set(r.date, Math.max(0, sameMonth ? r.orders - prev!.orders : r.orders));
    prev = r;
  }
  return out;
}

/** Orders in the last 14 days and the 14 before, from daily counts keyed by NPT date. */
export function orderWindows(daily: Map<string, number>, today: string): [number, number] {
  const t = Date.parse(`${today}T00:00:00Z`);
  let cur = 0;
  let prev = 0;
  for (const [d, n] of daily) {
    const ago = Math.round((t - Date.parse(`${d}T00:00:00Z`)) / DAY);
    if (ago >= 0 && ago < 14) cur += n;
    else if (ago >= 14 && ago < 28) prev += n;
  }
  return [cur, prev];
}

// ---------- shop health score ----------

export const HEALTH_PARTS = ["activity", "sales", "setup", "billing", "support"] as const;
export type HealthPart = (typeof HEALTH_PARTS)[number];
export const HEALTH_MAX: Record<HealthPart, number> = {
  activity: 30,
  sales: 25,
  setup: 15,
  billing: 20,
  support: 10,
};
export type HealthBand = "healthy" | "watch" | "at_risk";
export type HealthReason =
  | "inactive"
  | "quiet"
  | "no_orders"
  | "orders_down"
  | "setup_low"
  | "grace"
  | "renewal_short"
  | "renewal_off"
  | "tickets_open"
  | "tickets_overdue"
  | "paused";

export interface HealthFacts {
  lifecycle: OrgLifecycleState;
  now: number;
  last_active_ms: number | null;
  created_ms: number;
  orders_14d: number;
  orders_prev_14d: number;
  setup_percent: number;
  plan_kind: "base" | "trial" | "paid";
  plan_state: string;
  renewal_soon: boolean;
  renewal_short: boolean;
  auto_renew: boolean;
  open_tickets: number;
  overdue_tickets: number;
}

export interface ShopHealth {
  /** 0–100. */
  score: number;
  /** null for a suspended or closed shop. */
  band: HealthBand | null;
  parts: Record<HealthPart, number>;
  /** What pulls the score down, worst first. */
  reasons: HealthReason[];
}

export function shopHealth(f: HealthFacts): ShopHealth {
  const reasons: HealthReason[] = [];
  const days = (f.now - (f.last_active_ms ?? f.created_ms)) / DAY;
  const activity = days <= 1 ? 30 : days <= 3 ? 25 : days <= 7 ? 18 : days <= 14 ? 8 : 0;
  if (days > 14) reasons.push("inactive");
  else if (days > 7) reasons.push("quiet");

  let sales: number;
  const young = f.now - f.created_ms < 14 * DAY;
  if (f.orders_14d === 0 && f.orders_prev_14d === 0) {
    sales = young ? 12 : 4;
    if (!young) reasons.push("no_orders");
  } else if (f.orders_prev_14d === 0) sales = 25;
  else {
    const ratio = f.orders_14d / f.orders_prev_14d;
    sales = ratio >= 1 ? 25 : ratio >= 0.75 ? 20 : ratio >= 0.5 ? 12 : 4;
    if (ratio < 0.75) reasons.push("orders_down");
  }

  const setup = Math.round((Math.min(100, Math.max(0, f.setup_percent)) / 100) * HEALTH_MAX.setup);
  if (f.setup_percent < 60) reasons.push("setup_low");

  let billing = f.plan_kind === "paid" ? 20 : f.plan_kind === "trial" ? 14 : 12;
  if (f.plan_kind === "paid") {
    if (f.plan_state === "grace") {
      billing = 2;
      reasons.unshift("grace");
    } else if (f.renewal_short) {
      billing = 8;
      reasons.push("renewal_short");
    } else if (f.renewal_soon && !f.auto_renew) {
      billing = 12;
      reasons.push("renewal_off");
    }
  }

  let support = 10;
  if (f.overdue_tickets > 0) {
    support = 2;
    reasons.push("tickets_overdue");
  } else if (f.open_tickets > 0) {
    support = 6;
    reasons.push("tickets_open");
  }

  const parts: Record<HealthPart, number> = { activity, sales, setup, billing, support };
  const score = HEALTH_PARTS.reduce((n, k) => n + parts[k], 0);
  if (f.lifecycle === "suspended" || f.lifecycle === "closed")
    return { score, band: null, parts, reasons: ["paused"] };
  return {
    score,
    band: score >= 70 ? "healthy" : score >= 40 ? "watch" : "at_risk",
    parts,
    reasons,
  };
}
